import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  FileText, Send, Printer, Check, X,
  MessageCircle, Mail, Download, Eye,
  ArrowLeft, Plus, Share2, CheckCircle
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

import Button from '../components/ui/Button'
import { OrdemServico } from '../types'
import { formatValor } from '../services/ordensServico'
import ordensServicoService from '../services/ordensServico'
import { useToast } from '../components/Toast'

const OrdemServicoFechamentoSuccessPage: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { success, error, info } = useToast()
  const [sending, setSending] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string>('')

  // Buscar dados da OS
  const { data: ordemServico, isLoading } = useQuery({
    queryKey: ['ordem-servico', id],
    queryFn: () => ordensServicoService.getById(parseInt(id!)),
    enabled: !!id
  })

  // Gerar URL do PDF de fechamento automaticamente ao carregar
  useEffect(() => {
    const gerarPdfUrl = async () => {
      if (!ordemServico) return

      try {
        // Gerar URL do PDF com token de autenticação
        const token = localStorage.getItem('token')
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4050/api'
        const url = `${apiUrl}/ordens-servico/${ordemServico.id}/cupom/fechamento?token=${token}`
        setPdfUrl(url)
      } catch (err) {
        console.error('Erro ao gerar URL do PDF:', err)
        error('Erro ao carregar PDF do cupom')
      }
    }

    gerarPdfUrl()
  }, [ordemServico, error])

  const handleSendWhatsApp = async () => {
    if (!ordemServico) return

    try {
      setSending(true)
      const telefone = ordemServico.cliente.telefonePrincipal?.replace(/\D/g, '')
      if (!telefone) {
        error('Cliente não possui telefone cadastrado')
        return
      }

      const mensagem = `Olá ${ordemServico.cliente.nome},\n\nSua ordem de serviço foi concluída! ✅\n\n📋 *ORDEM DE SERVIÇO ${ordemServico.numero}*\n\n*Equipamento:* ${ordemServico.equipamento.modelo?.marca?.nome || ''} ${ordemServico.equipamento.modelo?.nome || 'N/A'}\n*Diagnóstico:* ${ordemServico.diagnostico || 'N/A'}\n*Solução:* ${ordemServico.solucao || 'N/A'}\n*Valor Total:* ${formatValor(ordemServico.valorFinal)}\n\nSeu equipamento está pronto para retirada!\n\nAtenciosamente,\nEquipe Técnica`

      const whatsappUrl = `https://wa.me/${telefone}?text=${encodeURIComponent(mensagem)}`
      window.open(whatsappUrl, '_blank')

      success('Mensagem WhatsApp preparada com sucesso')
    } catch (err) {
      error('Erro ao preparar WhatsApp')
    } finally {
      setSending(false)
    }
  }

  const handleSendEmail = async () => {
    if (!ordemServico) return

    try {
      setSending(true)

      if (!ordemServico.cliente.email) {
        error('Cliente não possui email cadastrado')
        return
      }

      const subject = `Ordem de Serviço ${ordemServico.numero} - Concluída`
      const body = `Prezado(a) ${ordemServico.cliente.nome},

Informamos que o reparo do seu equipamento foi concluído com sucesso!

ORDEM DE SERVIÇO: ${ordemServico.numero}
Data de Conclusão: ${ordemServico.dataConclusao ? new Date(ordemServico.dataConclusao).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR')}

Equipamento: ${ordemServico.equipamento.modelo?.marca?.nome || ''} ${ordemServico.equipamento.modelo?.nome || 'N/A'}
Número de Série: ${ordemServico.equipamento.numeroSerie || 'N/A'}

Diagnóstico: ${ordemServico.diagnostico || 'N/A'}
Solução Aplicada: ${ordemServico.solucao || 'N/A'}

Valor Total: ${formatValor(ordemServico.valorFinal)}

Seu equipamento está pronto para retirada. Em anexo segue o recibo para seus registros.

Atenciosamente,
Equipe Técnica`

      const mailtoUrl = `mailto:${ordemServico.cliente.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
      window.open(mailtoUrl, '_blank')

      success('Email preparado com sucesso')
      info('Anexe o PDF manualmente no seu cliente de email')
    } catch (err) {
      error('Erro ao preparar email')
    } finally {
      setSending(false)
    }
  }

  const handlePrint = async () => {
    if (!ordemServico || !pdfUrl) return

    try {
      const printWindow = window.open(pdfUrl, '_blank')
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
        success('Cupom PDF aberto para impressão')
      } else {
        error('Bloqueador de pop-up impediu a abertura do PDF')
      }
    } catch (err) {
      console.error('Erro ao imprimir cupom:', err)
      error('Erro ao imprimir cupom PDF')
    }
  }

  const handleDownloadPDF = async () => {
    if (!ordemServico) return

    try {
      // Abrir PDF de recibo de fechamento em nova aba
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4050/api'
      const pdfUrl = `${apiUrl}/pdf/ordem-servico/${ordemServico.id}/recibo`
      window.open(pdfUrl, '_blank')
      success('PDF aberto em nova aba')
    } catch (err) {
      error('Erro ao abrir PDF')
    }
  }


  const handleNewOS = () => {
    navigate('/ordens-servico?new=true')
  }

  const handleGoToOS = () => {
    navigate('/ordens-servico')
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando ordem de serviço...</p>
        </div>
      </div>
    )
  }

  if (!ordemServico) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Ordem de serviço não encontrada</p>
          <Button
            className="mt-4"
            onClick={handleGoToOS}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Voltar
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header de Sucesso */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">
                Ordem de Serviço Concluída com Sucesso!
              </h1>
              <p className="text-gray-600 mt-1">
                OS {ordemServico.numero} foi fechada e está pronta para entrega ao cliente
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleGoToOS}
                leftIcon={<ArrowLeft className="h-4 w-4" />}
              >
                Voltar à Lista
              </Button>
              <Button
                onClick={handleNewOS}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Nova OS
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna Principal - Preview do Cupom */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Recibo de Fechamento
                  </h2>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleDownloadPDF}
                      leftIcon={<Eye className="h-4 w-4" />}
                    >
                      Ver PDF A4
                    </Button>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {/* Preview do Cupom Térmico PDF */}
                <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ height: '800px' }}>
                  {pdfUrl ? (
                    <object
                      data={pdfUrl}
                      type="application/pdf"
                      className="w-full h-full"
                    >
                      <iframe
                        src={pdfUrl}
                        className="w-full h-full border-0"
                        title="Preview do Cupom de Fechamento"
                      />
                    </object>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                        <p className="mt-2 text-gray-600">Gerando recibo PDF...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Coluna Lateral - Informações e Ações */}
          <div className="space-y-6">
            {/* Informações da OS */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Resumo do Fechamento
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Número</span>
                  <p className="text-sm font-semibold text-gray-900">{ordemServico.numero}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Cliente</span>
                  <p className="text-sm text-gray-900">{ordemServico.cliente.nome}</p>
                  {ordemServico.cliente.telefonePrincipal && (
                    <p className="text-xs text-gray-500">{ordemServico.cliente.telefonePrincipal}</p>
                  )}
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Equipamento</span>
                  <p className="text-sm text-gray-900">
                    {ordemServico.equipamento.modelo?.marca?.nome} {ordemServico.equipamento.modelo?.nome}
                  </p>
                  {ordemServico.equipamento.numeroSerie && (
                    <p className="text-xs text-gray-500">S/N: {ordemServico.equipamento.numeroSerie}</p>
                  )}
                </div>
                {ordemServico.diagnostico && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase">Diagnóstico</span>
                    <p className="text-sm text-gray-900">{ordemServico.diagnostico}</p>
                  </div>
                )}
                {ordemServico.solucao && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase">Solução</span>
                    <p className="text-sm text-gray-900">{ordemServico.solucao}</p>
                  </div>
                )}
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">Valor Final</span>
                  <p className="text-lg font-bold text-green-600">
                    {formatValor(ordemServico.valorFinal)}
                  </p>
                </div>
              </div>
            </div>

            {/* Ações de Envio */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Share2 className="h-5 w-5" />
                Notificar Cliente
              </h3>
              <div className="space-y-3">
                <Button
                  fullWidth
                  variant="outline"
                  onClick={handleSendWhatsApp}
                  disabled={sending || !ordemServico.cliente.telefonePrincipal}
                  leftIcon={<MessageCircle className="h-4 w-4" />}
                >
                  Enviar por WhatsApp
                </Button>
                <Button
                  fullWidth
                  variant="outline"
                  onClick={handleSendEmail}
                  disabled={sending || !ordemServico.cliente.email}
                  leftIcon={<Mail className="h-4 w-4" />}
                >
                  Enviar por Email
                </Button>
                <Button
                  fullWidth
                  variant="outline"
                  onClick={handlePrint}
                  leftIcon={<Printer className="h-4 w-4" />}
                >
                  Imprimir Recibo
                </Button>
              </div>

              {!ordemServico.cliente.telefonePrincipal && !ordemServico.cliente.email && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    ⚠️ Cliente não possui contatos cadastrados. Atualize o cadastro para habilitar o envio.
                  </p>
                </div>
              )}
            </div>

            {/* Dicas */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-green-900 mb-2">
                ✅ Próximos Passos
              </h4>
              <ul className="text-xs text-green-700 space-y-1">
                <li>• Equipamento está pronto para entrega</li>
                <li>• Envie o recibo ao cliente por WhatsApp ou Email</li>
                <li>• Imprima o recibo para arquivo físico</li>
                <li>• Notifique o cliente sobre a conclusão</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrdemServicoFechamentoSuccessPage
